import type { ServiceType } from '@mathflat/domain/Student'
import clsx from 'clsx'
import { observer } from 'mobx-react'
import { useEffect, useRef, useState } from 'react'

import { Badge } from '@/component/atoms/Badge'
import { Icon } from '@/component/atoms/Icon'
import { useEnglishflatConnect } from '@/hooks/useEnglishflatConnect'
import { useScienceflatConnect } from '@/hooks/useScienceflatConnect'
import { mixpanelClient } from '@/utils/trackings/mixpanel'

import { getSubjectAvailability } from './subjectAvailability'
import type { SubjectMenuItem } from './subjectMenu'
import {
  getCurrentSubjectLabel,
  getSubjectCarousel,
  getSubjectMenuItems,
  usesTextLogo,
} from './subjectMenu'
import S from './SubjectSwitch.style'

// 캐러셀 단계: 0=쉼(현재 과목+chevron), 1~=각 과목 로고 순환. 쉼 단계만 길게 머문다(ms).
const CAROUSEL_REST_PHASE_DURATION_MS = 3500
const CAROUSEL_SUBJECT_PHASE_DURATION_MS = 1000

const SubjectSwitch = observer(() => {
  const wrapRef = useRef<HTMLDivElement>(null)
  const faceRef = useRef<HTMLSpanElement>(null)
  const flipAnimationRef = useRef<Animation | null>(null)
  const [isOpened, setIsOpened] = useState(false)

  const handleScienceflatConnect = useScienceflatConnect()
  const handleEnglishflatConnect = useEnglishflatConnect()

  const availability = getSubjectAvailability()

  const menuItems = getSubjectMenuItems(availability)
  const subjectCarousel = getSubjectCarousel(availability)
  const carouselPhaseCount = subjectCarousel.length + 1
  const currentLabel = getCurrentSubjectLabel()

  const [carouselPhase, setCarouselPhase] = useState(0)
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false)

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)')
    setPrefersReducedMotion(mediaQuery.matches)
    const handleChange = (event: MediaQueryListEvent) => setPrefersReducedMotion(event.matches)
    mediaQuery.addEventListener('change', handleChange)

    return () => mediaQuery.removeEventListener('change', handleChange)
  }, [])

  // 드롭다운이 닫혀 있고 모션 허용일 때만 트리거가 과목 로고를 자동 순환한다.
  useEffect(() => {
    if (isOpened || prefersReducedMotion) {
      setCarouselPhase(0)
      return
    }
    const timerId = window.setTimeout(
      () => setCarouselPhase((phase) => (phase + 1) % carouselPhaseCount),
      carouselPhase === 0 ? CAROUSEL_REST_PHASE_DURATION_MS : CAROUSEL_SUBJECT_PHASE_DURATION_MS,
    )

    return () => window.clearTimeout(timerId)
  }, [isOpened, prefersReducedMotion, carouselPhase, carouselPhaseCount])

  // 쉼 단계(0)는 현재 과목+chevron, 이후 단계는 각 과목 라벨+로고를 보여준다.
  const isTextLogo = (key: ServiceType) => usesTextLogo(key, availability.brand)

  // 스쿨플랫은 사이언스플랫·잉글리시플랫을 별도 서비스가 아닌 자기 과목으로 부르므로,
  // 항목 표기에 맞춰 헤더·aria-label 도 "과목" 으로 부른다.
  const isSchoolflat = availability.brand === 'schoolflat'
  const menuHeaderText = isSchoolflat ? '다른 과목으로 이동' : '다른 서비스로 이동'
  const triggerAriaLabel = isSchoolflat ? '다른 과목으로 전환' : '다른 서비스로 전환'

  const carouselSubject = subjectCarousel[carouselPhase - 1]
  const isShowingLogo = !isOpened && carouselPhase !== 0 && !!carouselSubject
  const faceLabel = isShowingLogo ? carouselSubject.subjectLabel : currentLabel
  const faceColor = isShowingLogo ? carouselSubject.color : undefined

  // 로고 <img> 를 단계마다 remount 하면 브라우저가 매번 로고를 재요청한다. face 는 유지한 채
  // flip 애니메이션만 Web Animations API 로 다시 재생해, 로고는 최초 1회만 로드되게 한다.
  useEffect(() => {
    if (prefersReducedMotion) return
    // isOpened 변경과 carouselPhase 리셋이 잇달아 트리거돼 animate 가 겹쳐 호출될 수 있어, 이전 것을 취소한다.
    flipAnimationRef.current?.cancel()
    flipAnimationRef.current =
      faceRef.current?.animate?.(
        [
          { opacity: 0, transform: 'rotateX(-90deg)' },
          { opacity: 1, transform: 'rotateX(0)' },
        ],
        { duration: 450, easing: 'ease' },
      ) ?? null
  }, [carouselPhase, isOpened, prefersReducedMotion])

  // 바깥 클릭 시 닫는다. 자기 영역(wrapRef) 내부 클릭은 토글/항목 선택을 위해 제외.
  // click 대신 mousedown 을 듣는다 — 캐러셀 flip 으로 트리거 내부가 remount 되면
  // click 시점엔 클릭 노드가 DOM 에서 분리돼 contains 가 false 가 되어 곧바로 닫히는 문제가 있다.
  useEffect(() => {
    const handleOutsideMouseDown = (event: MouseEvent) => {
      if (wrapRef.current?.contains(event.target as Node)) return
      setIsOpened(false)
    }
    document.addEventListener('mousedown', handleOutsideMouseDown)

    return () => {
      document.removeEventListener('mousedown', handleOutsideMouseDown)
    }
  }, [])

  const handleItemClick = async (item: SubjectMenuItem) => {
    if (item.key === 'SCIENCEFLAT') mixpanelClient.track('GNB_과목_사이언스플랫_click')
    if (item.key === 'ENGLISHFLAT') mixpanelClient.track('GNB_과목_잉글리시플랫_click')

    if (item.state === 'switchable') {
      // 메뉴를 먼저 닫는다 — 전환은 모달·새 탭으로 이어지고 드롭다운이 그 위에 남으면 안 된다.
      setIsOpened(false)
      await (item.key === 'ENGLISHFLAT' ? handleEnglishflatConnect() : handleScienceflatConnect())
      return
    }
    // current 는 동작 없음.
  }

  return (
    <div ref={wrapRef} css={S.wrap}>
      <button
        type="button"
        css={S.trigger}
        className={clsx(isOpened && 'opened')}
        aria-haspopup="menu"
        aria-expanded={isOpened}
        aria-label={triggerAriaLabel}
        onClick={() => {
          mixpanelClient.track('GNB_과목_click')
          setIsOpened((prev) => !prev)
        }}
      >
        <span ref={faceRef} css={S.face}>
          <span
            css={[S.label, isShowingLogo && S.labelWithLogo]}
            style={faceColor ? { color: faceColor } : undefined}
          >
            {faceLabel}
          </span>
          <span
            css={S.logoSlot}
            className={clsx(isShowingLogo && isTextLogo(carouselSubject.key) ? 'text-logo' : '')}
          >
            {/* opacity 전환만으로 로고를 바꿔 브라우저의 반복 재요청을 방지 */}
            {subjectCarousel.map((subject) =>
              isShowingLogo && carouselSubject.key === subject.key && isTextLogo(subject.key) ? (
                <span className="text" key={subject.key}>
                  OPEN
                </span>
              ) : (
                <img
                  key={subject.key}
                  css={[S.triggerLogo, subject.symbol.kind === 'tinted' && S.triggerLogoTinted]}
                  src={subject.symbol.url}
                  alt=""
                  style={{
                    opacity:
                      isShowingLogo &&
                      carouselSubject.key === subject.key &&
                      !isTextLogo(subject.key)
                        ? 1
                        : 0,
                    background:
                      subject.symbol.kind === 'tinted' ? subject.symbol.background : undefined,
                  }}
                />
              ),
            )}
            {!isShowingLogo && (
              <Icon name="chevron_down" size={16} color="var(--Colors-Gray-900)" />
            )}
          </span>
        </span>
      </button>

      {isOpened && (
        <div css={S.menu} role="menu">
          <p css={S.menuHeader}>{menuHeaderText}</p>
          {menuItems.map((item) => (
            <button
              key={item.key}
              type="button"
              role="menuitem"
              css={S.item}
              className={clsx(
                item.state === 'current' && 'current',
                item.state !== 'current' && 'actionable',
              )}
              aria-disabled={item.state === 'current'}
              onClick={() => void handleItemClick(item)}
            >
              <span css={S.itemMain}>
                <span
                  css={[S.symbol, item.symbol.kind === 'tinted' && S.symbolTinted]}
                  style={
                    item.symbol.kind === 'tinted'
                      ? { backgroundColor: item.symbol.background }
                      : undefined
                  }
                >
                  <img src={item.symbol.url} alt="" />
                </span>
                <span css={S.itemText}>
                  <span css={S.brandName}>{item.brandName}</span>
                  {item.subjectLabel && <span css={S.subjectLabel}>{item.subjectLabel}</span>}
                </span>
              </span>
              <span css={S.itemAside}>
                {item.state === 'current' && <span css={S.usingText}>이용 중</span>}
                {item.state === 'switchable' && (
                  <>
                    {availability.brand === 'schoolflat' ? (
                      <span css={S.usingText}>바로가기</span>
                    ) : (
                      item.badge && (
                        <Badge
                          size="xs"
                          radius="circle"
                          color="var(--White)"
                          backgroundColor={item.badge.background}
                          borderColor={item.badge.background}
                        >
                          {item.badge.label}
                        </Badge>
                      )
                    )}
                    <Icon name="chevron_right" size={16} color="var(--Colors-Gray-600)" />
                  </>
                )}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  )
})

export default SubjectSwitch
